import { cloneDeep, isEqual } from "lodash";
import { useRef } from "react";
import { FieldValues } from "react-hook-form";
import { useEventCallback } from "usehooks-ts";

export const useCompareFormValues = <FormType extends FieldValues>(
  formInitialValues: FormType,
): { compareFormValues: (newValues: FormType) => string[] } => {
  const prevValuesRef = useRef<FormType>(cloneDeep(formInitialValues));

  const findChangedFields = (
    prev: FormType | null,
    current: FormType,
  ): string[] => {
    if (!prev) {
      return [];
    }

    const changed: string[] = [];
    const prevKeys = new Set(Object.keys(prev || {}));
    const currentKeys = new Set(Object.keys(current || {}));
    const allKeys = new Set([...prevKeys, ...currentKeys]);

    for (const key of allKeys) {
      const prevValue = prev?.[key];
      const currentValue = current?.[key];

      // Doing deep comparison using isEqual from lodash
      if (!isEqual(prevValue, currentValue) && Boolean(key.trim())) {
        changed.push(key);
      }
    }

    return changed;
  };

  const compareFormValues = useEventCallback((newValues: FormType) => {
    const changed = findChangedFields(prevValuesRef.current, newValues);
    prevValuesRef.current = cloneDeep(newValues);

    return changed;
  });

  return {
    compareFormValues,
  };
};
